import type { GridCells, Log, PresentationTaskRunner } from "@slot/presentation";
import type { GridPresenter } from "@slot/mechanics-core";
import type { MergeSymbolsInput, MergeSymbolsTarget, SymbolMergeOutput } from "./MergeSymbolsActionHandler";
import type { SymbolMerge } from "./SymbolMerge";

/**
 * The merge's sequence (`merge_symbols`): the action is checked against the grid before anything plays — every cell
 * inside it and showing a symbol — then the cue, the motion (`SymbolMerge`), and the removal of the consumed cells
 * through the grid presenter the mechanic runs on. The motion has already emptied those cells, so the base layer finds
 * nothing left to remove: the removal records them for the collapse that drops into them, and the win highlight ends
 * with it (its dim fades back, its labels go). A beat of stillness closes it.
 */
export class MergePresenter implements MergeSymbolsTarget {
  public constructor(
    private readonly deps: {
      grid: GridCells;
      merge: SymbolMerge;
      /** The composite grid presenter of the removal-and-refill mechanic the merge runs on (the cascade's, the tumble's). */
      gridPresenter: GridPresenter;
      runner: PresentationTaskRunner;
      postMergeHoldSeconds: number;
      /** The merge cue (the title plays its sound and haptic); omit to play nothing. */
      onMerge?: () => void;
      log: Log;
    },
  ) {}

  public async mergeSymbols(input: MergeSymbolsInput): Promise<void> {
    const consumed = this.assertOnGrid(input.merges);
    this.deps.log(`[Merge] ${input.merges.map((m) => `${m.consumedPositionIndices.join("+")}→${m.targetPositionIndex}=${m.resultSymbolId}`).join(" ")}`);
    this.deps.onMerge?.();
    await this.deps.merge.merge(input.merges);
    const size = this.deps.grid.gridColumnCount * this.deps.grid.gridRowCount;
    await this.deps.gridPresenter.removeSymbolsByMask(Array.from({ length: size }, (_, i) => (consumed.has(i) ? "1" : "0")).join(""));
    if (this.deps.postMergeHoldSeconds > 0) await this.deps.runner.sleep(this.deps.postMergeHoldSeconds);
  }

  /** Every cell inside the grid and showing a symbol, else the round disagrees with the grid: nothing plays. Returns the consumed cells. */
  private assertOnGrid(merges: readonly SymbolMergeOutput[]): Set<number> {
    const { grid } = this.deps;
    const size = grid.gridColumnCount * grid.gridRowCount;
    const consumed = new Set<number>();
    for (const merge of merges) {
      for (const index of [merge.targetPositionIndex, ...merge.consumedPositionIndices]) {
        if (index >= size) throw new Error(`[Merge] cell ${index} is outside the ${grid.gridColumnCount}×${grid.gridRowCount} grid`);
        if (!grid.getSymbolViewAtIndex(index)) throw new Error(`[Merge] cell ${index} shows no symbol`);
      }
      for (const index of merge.consumedPositionIndices) consumed.add(index);
    }
    return consumed;
  }
}
